/**
 * Sign-in resume marker + server session probe (LEV-1925).
 *
 * The marker is set on `/onboarding/complete`, after sign-in succeeds, right
 * before it routes back to the landing page — not before the sign-in
 * redirect. It tells the landing chat to auto-restore the founder's
 * conversation on that return trip instead of leaving them claimed straight
 * into "We lost your idea".
 *
 * MUST NOT import handoff.ts or session.ts. Several test files mock those two
 * modules by listing only the exports they use
 * (`vi.mock("@/lib/onboarding/session", () => ({ save: fn, ... }))`); if this
 * file imported from either, those same tests would hand it that same partial
 * mock, and any export this file needed but the test didn't list would come
 * back `undefined` at runtime. Callers combine this module's output with
 * those modules' peek* helpers themselves.
 */
import { SS_ONBOARDING_RESUME_CHAT } from "@/lib/storage-keys";
import {
  hasMinimumBigIdeaData,
  normalizeOnboardingResult,
} from "@/lib/onboarding/schema";
import type { ServerSessionProbe } from "@/lib/onboarding/completion";

/** Longer than any real sign-in round trip, short enough that a days-old abandoned tab doesn't reopen chat. */
const RESUME_MARKER_TTL_MS = 10 * 60 * 1000;

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

interface ResumeMarkerPayload {
  markedAt: number;
  /** Set by the first successful claimResumeRestoreTracking() call; its presence is what makes the claim once-per-marker instead of once-per-mount. */
  trackedAt?: number;
}

/** Both accessors funnel through here so `trackedAt` survives every re-read/re-write of the marker. */
function readResumeMarker(): ResumeMarkerPayload | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(SS_ONBOARDING_RESUME_CHAT);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ResumeMarkerPayload>;
    if (typeof parsed.markedAt !== "number" || !Number.isFinite(parsed.markedAt)) {
      return null;
    }
    return {
      markedAt: parsed.markedAt,
      trackedAt: typeof parsed.trackedAt === "number" ? parsed.trackedAt : undefined,
    };
  } catch {
    // benign: sessionStorage unavailable, or the stored value predates this JSON shape — no marker means no resume, which is the safe default.
    return null;
  }
}

function writeResumeMarker(payload: ResumeMarkerPayload): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(SS_ONBOARDING_RESUME_CHAT, JSON.stringify(payload));
  } catch {
    /* benign: sessionStorage unavailable (private mode, blocked storage) */
  }
}

/** Set on `/onboarding/complete`, after sign-in, right before the redirect back to the landing page. */
export function markResumeChat(now: number = Date.now()): void {
  writeResumeMarker({ markedAt: now });
}

/** True only while the marker exists and is within its expiry window. */
export function peekResumeChat(now: number = Date.now()): boolean {
  const marker = readResumeMarker();
  if (!marker) return false;
  return now - marker.markedAt < RESUME_MARKER_TTL_MS;
}

/**
 * Claims the marker for analytics tracking, once. The restore itself repeats
 * on every mount (LandingHero remounts this component once Clerk resolves),
 * but the `onboarding_chat_restored` event must fire once per sign-in, not
 * once per remount — callers gate that event on this returning true.
 */
export function claimResumeRestoreTracking(now: number = Date.now()): boolean {
  const marker = readResumeMarker();
  if (!marker) return false;
  if (now - marker.markedAt >= RESUME_MARKER_TTL_MS) return false;
  if (marker.trackedAt !== undefined) return false;
  writeResumeMarker({ markedAt: marker.markedAt, trackedAt: now });
  return true;
}

export function clearResumeChat(): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(SS_ONBOARDING_RESUME_CHAT);
  } catch {
    /* benign: sessionStorage unavailable (private mode, blocked storage) */
  }
}

/**
 * Read the cookie's onboarding session and resolve it to a `ServerSessionProbe`.
 * Any non-200/404/409 response, a malformed body, or a thrown fetch all
 * collapse to "error" — `shouldResumeChat` treats a failed probe as resumable,
 * since a chat the founder can't get back to is the idea lost for good.
 */
export async function probeOnboardingSession(
  signal?: AbortSignal,
): Promise<ServerSessionProbe> {
  try {
    const res = await fetch("/api/onboarding/session", {
      method: "GET",
      credentials: "same-origin",
      signal,
    });
    if (res.status === 404) return "none";
    if (res.status === 409) return "claimed";
    if (!res.ok) return "error";
    const body = (await res.json()) as { result?: unknown };
    const normalized = normalizeOnboardingResult(body?.result);
    return hasMinimumBigIdeaData(normalized) ? "usable" : "unusable";
  } catch {
    // expected: network failure or a malformed body — shouldResumeChat treats "error" as resumable, so this is not a lost signal, just an unresolved one.
    return "error";
  }
}
